import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import type { SidekickActingOrganizationScope } from '../api/actingOrganization';
import {
  discardConversationBranch,
  selectPendingHostUserMessage,
} from '../state/aiSidekickChatSlice';
import type { StateWithSidekickChat } from '../state/chatTypes';

export type PendingEditedTurn = {
  conversationId: string;
  content: string;
  canDiscardBackendConversation: boolean;
  organizationScope: SidekickActingOrganizationScope;
  resolve: (accepted: boolean) => void;
};

type UsePendingEditedTurnParams = {
  activeConversationId: string | null;
  sendMessage: (content: string) => boolean;
  discardConversation: (
    conversationId: string,
    organizationScope: SidekickActingOrganizationScope
  ) => Promise<void>;
  onEditedTurnRejected?: () => void;
};

type PendingEditedTurnRef = {
  current: PendingEditedTurn | null;
};

/**
 * Submits an edited turn only after its backend branch becomes active and
 * cleans up branches whose deferred submission cannot proceed.
 * @param params - Active branch and conversation operations
 * @returns Mutable pending-turn handoff used by branch creation
 */
export const usePendingEditedTurn = ({
  activeConversationId,
  sendMessage,
  discardConversation,
  onEditedTurnRejected,
}: UsePendingEditedTurnParams): PendingEditedTurnRef => {
  const dispatch = useDispatch();
  const store = useStore<StateWithSidekickChat>();
  const pendingHostUserMessage = useSelector(selectPendingHostUserMessage);
  const pendingEditedTurnRef = useRef<PendingEditedTurn | null>(null);
  const discardPendingTurn = useCallback(
    (pendingEditedTurn: PendingEditedTurn): void => {
      dispatch(discardConversationBranch(pendingEditedTurn.conversationId));
      if (pendingEditedTurn.canDiscardBackendConversation) {
        void discardConversation(
          pendingEditedTurn.conversationId,
          pendingEditedTurn.organizationScope
        );
      }
    },
    [discardConversation, dispatch]
  );
  const discardPendingTurnWhenIdle = useCallback(
    (pendingEditedTurn: PendingEditedTurn): void => {
      let didDiscard = false;
      let unsubscribe = (): void => undefined;
      const discardIfIdle = (): void => {
        if (didDiscard || store.getState().aiSidekickChat.isStreaming) {
          return;
        }
        didDiscard = true;
        unsubscribe();
        discardPendingTurn(pendingEditedTurn);
      };
      // The store owns this listener so deferred cleanup survives unmount.
      unsubscribe = store.subscribe(discardIfIdle);
      discardIfIdle();
    },
    [discardPendingTurn, store]
  );

  useEffect(
    () => () => {
      const pendingEditedTurn = pendingEditedTurnRef.current;
      pendingEditedTurnRef.current = null;
      if (pendingEditedTurn !== null) {
        discardPendingTurnWhenIdle(pendingEditedTurn);
      }
      pendingEditedTurn?.resolve(false);
    },
    [discardPendingTurnWhenIdle]
  );

  useLayoutEffect(() => {
    const pendingEditedTurn = pendingEditedTurnRef.current;
    if (pendingEditedTurn === null) {
      return;
    }
    pendingEditedTurnRef.current = null;
    if (pendingEditedTurn.conversationId !== activeConversationId) {
      discardPendingTurnWhenIdle(pendingEditedTurn);
      pendingEditedTurn.resolve(false);
      return;
    }
    if (pendingHostUserMessage !== null) {
      discardPendingTurnWhenIdle(pendingEditedTurn);
      pendingEditedTurn.resolve(false);
      return;
    }
    const accepted = sendMessage(pendingEditedTurn.content);
    if (!accepted) {
      discardPendingTurnWhenIdle(pendingEditedTurn);
      onEditedTurnRejected?.();
    }
    pendingEditedTurn.resolve(accepted);
  }, [
    activeConversationId,
    discardPendingTurnWhenIdle,
    onEditedTurnRejected,
    pendingHostUserMessage,
    sendMessage,
  ]);

  return pendingEditedTurnRef;
};
